import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import LocationDisplay from '../components/LocationDisplay';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import VenueHours from '../components/VenueHours';
import './BirthdayVenuesPage.css';
import './SharedCardStyles.css';

interface Venue { id: string; slug: string; name: string; description?: string; short_description: string; venue_type: string; address?: string; city: string; capacity_min?: number; capacity_max?: number; price_range_min?: number; price_range_max?: number; price_description?: string; activities?: string[]; indoor: boolean; catering_included: boolean; party_host_included: boolean; website_url?: string; booking_url?: string; }

const BirthdayVenuesPage: React.FC = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { fetchVenues(); }, []);

  const handleBooking = (url: string | undefined) => { if (url) window.open(url, '_blank'); };

  const extractRating = (description: string): { rating: string; reviews: string } | null => {
    const match = description.match(/Rated ([\d.]+) stars \((\d+) reviews?\)/);
    if (match) return { rating: match[1], reviews: match[2] };
    return null;
  };

  const generateDescription = (venue: Venue): string => {
    const name = venue.name.toLowerCase();
    const venueTypeLower = (venue.venue_type || '').toLowerCase();
    
    // Extract venue type from name or venue_type field
    let description = '';
    
    // Check name keywords first
    if (name.includes('mini golf') || name.includes('putt')) {
      description = 'Enjoy mini golf and family fun at this exciting venue';
    } else if (name.includes('arcade') || name.includes('game')) {
      description = 'Arcade games and entertainment for all ages';
    } else if (name.includes('trampoline') || name.includes('jump')) {
      description = 'High-energy trampoline park with jumping zones and activities';
    } else if (name.includes('bowling')) {
      description = 'Bowling lanes with party packages and food options';
    } else if (name.includes('laser') || name.includes('tag')) {
      description = 'Laser tag arena with exciting team missions';
    } else if (name.includes('skate') || name.includes('roller')) {
      description = 'Skating rink with music, lights, and party areas';
    } else if (name.includes('museum')) {
      description = 'Interactive museum with hands-on exhibits';
    } else if (name.includes('playground') || name.includes('play')) {
      description = 'Indoor playground with safe play areas for kids';
    } else if (name.includes('swim') || name.includes('aqua')) {
      description = 'Swimming facility offering party packages and pool fun';
    } else if (name.includes('martial') || name.includes('karate')) {
      description = 'Martial arts facility with party programs';
    } else if (name.includes('escape')) {
      description = 'Escape room adventures with challenging puzzles';
    } else if (name.includes('axe')) {
      description = 'Axe throwing venue with guided sessions';
    } 
    // Check venue type if no name match
    else if (venueTypeLower.includes('attraction')) {
      description = 'Entertainment attraction perfect for celebrations';
    } else if (venueTypeLower.includes('activity')) {
      description = 'Activity center with organized programs and events';
    } 
    // Default based on what we know
    else if (venue.indoor) {
      description = 'Indoor event space perfect for birthday celebrations';
    } else {
      description = 'Private event venue with party hosting services';
    }
    
    const capacity = venue.capacity_min && venue.capacity_max 
      ? `Accommodates ${venue.capacity_min}-${venue.capacity_max} guests`
      : 'Flexible capacity for various group sizes';
    
    return `${description}. ${capacity}. Contact them for party packages and booking details.`;
  };

  const fetchVenues = async () => {
    try {
      setLoading(true); setError(null);
      let query = supabase.from('venues').select('*').eq('status', 'active');
      const selectedCity = localStorage.getItem('selectedCity');
      if (selectedCity) query = query.eq('city', selectedCity);
      const { data, error } = await query;
      if (error) throw error;
      // Sort by popularity: rating × reviews (descending)
      const sortedVenues = (data || []).sort((a, b) => {
        const scoreA = (a.rating_avg || 0) * (a.rating_count || 0);
        const scoreB = (b.rating_avg || 0) * (b.rating_count || 0);
        return scoreB - scoreA; // Highest score first
      });
      setVenues(sortedVenues);
    } catch (err: any) { setError(err.message || 'Failed to load birthday venues'); } finally { setLoading(false); }
  };

  if (loading) return <div className="venues-container"><div className="loading"><div className="spinner"></div><p>Loading birthday party venues...</p></div></div>;
  if (error) return <div className="venues-container"><div className="error"><p>😕 Oops! {error}</p><button onClick={fetchVenues} className="retry-btn">Try Again</button></div></div>;

  return (
    <div className="venues-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      <LocationDisplay />
      <div className="venues-header"><h1>🎂 Birthday Party Venues</h1><p>Amazing places to celebrate your child's special day</p></div>
      {venues.length === 0 ? <div className="no-venues"><p>🎉 No venues found. Check back soon!</p></div> : (
        <div className="venues-list">
          {venues.filter(venue => venue.id && venue.id !== 'null' && venue.id !== 'undefined').map(venue => (
            <div key={venue.id} className="venue-card">
              <div className="favorite-button-wrapper"><FavoriteButton itemId={venue.id} itemType="venue" size="small" /></div>
              <div className="venue-content">
                <div className="venue-header-section">
                  <h3 className="venue-name">{venue.name}</h3>
                  {extractRating(venue.short_description) && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.name + ' ' + venue.city)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="venue-rating"
                      title="View on Google Maps"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="stars">⭐</span>
                      <span className="rating-value">{extractRating(venue.short_description)!.rating}</span>
                      <span className="rating-count">({extractRating(venue.short_description)!.reviews} reviews)</span>
                    </a>
                  )}
                </div>
                <p className="venue-type">{venue.venue_type}</p>
                <p className="venue-description-text">{generateDescription(venue)}</p>
                <VenueHours description={venue.description || venue.short_description} />
                <div className="venue-details">
                  <div className="detail"><span className="detail-icon">👥</span><span>{venue.capacity_min}-{venue.capacity_max} guests</span></div>
                  <div className="detail"><span className="detail-icon">💰</span><span>${venue.price_range_min}-${venue.price_range_max}</span></div>
                  <div className="detail">
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        venue.address 
                          ? `${venue.name} ${venue.address}, ${venue.city}`.trim()
                          : `${venue.name}, ${venue.city}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="location-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📍 {venue.address || venue.city}
                    </a>
                  </div>
                </div>
                {venue.activities && venue.activities.length > 0 && <div className="venue-activities"><strong>Activities:</strong> {venue.activities.join(', ')}</div>}
                <div className="venue-features">
                  {venue.catering_included && <span className="feature-badge">🍕 Catering</span>}
                  {venue.party_host_included && <span className="feature-badge">🎈 Party Host</span>}
                  {venue.indoor && <span className="feature-badge">🏠 Indoor</span>}
                </div>
                <div className="venue-actions">
                  <button 
                    className={`action-button icon-only ${!venue.website_url ? 'disabled' : ''}`}
                    onClick={() => handleBooking(venue.website_url)}
                    disabled={!venue.website_url}
                    title="Visit Website"
                    aria-label="Visit Website"
                  >
                    🌐
                  </button>
                  {venue.booking_url && (
                    <button 
                      className="action-button icon-only"
                      onClick={() => handleBooking(venue.booking_url)}
                      title="Book Party"
                      aria-label="Book Party"
                    >
                      🎉
                    </button>
                  )}
                  <ShareButton 
                    title={venue.name}
                    description={venue.short_description}
                    url={`${window.location.origin}/venue/${venue.slug}`}
                    location={`${venue.name}, ${venue.city}`}
                    size="small"
                    showLabel={false}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BirthdayVenuesPage;
